import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mfaService } from '../../services/mfaService';
import { supabase } from '../../lib/supabase';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import MfaVerificationStep from './components/MfaVerificationStep';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, signOut } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showMfaStep, setShowMfaStep] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (serverError) {
      setServerError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setServerError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);

      if (error) {
        setServerError(error?.message || 'Invalid email or password');
        setIsSubmitting(false);
        return;
      }

      // Get user profile to check role
      const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', data?.user?.id)?.single();

      // Check if user is owner and has MFA enabled
      const { data: factors } = await mfaService?.listFactors();
      
      if (factors && factors?.length > 0) {
        // MFA is enabled, show verification step
        setMfaFactorId(factors?.[0]?.id);
        setShowMfaStep(true);
        setIsSubmitting(false);
        return;
      }

      // No MFA, redirect based on role
      if (profile?.role === 'admin') {
        navigate('/admin-platform-management');
      } else {
        navigate('/home-page');
      }
    } catch (error) {
      setServerError('An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleMfaVerified = async () => {
    // Get user profile to check role after MFA verification
    const { data: { user } } = await supabase?.auth?.getUser();
    const { data: profile } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
    
    if (profile?.role === 'admin') {
      navigate('/admin-platform-management');
    } else {
      navigate('/home-page');
    }
  };

  const handleMfaCancel = async () => {
    await signOut();
    setShowMfaStep(false);
    setMfaFactorId('');
    setFormData({ email: '', password: '' });
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/home-page" className="inline-flex items-center space-x-3 mb-6">
            <img 
              src="/assets/images/ChatGPT_Image_Feb_3_2026_04_58_59_PM-1770675660365.png" 
              alt="Draggin Trailers Logo" 
              className="w-12 h-12 object-contain rounded-md"
            />
            <span className="font-heading font-bold text-2xl text-foreground">Draggin Trailers</span>
          </Link>
          <h1 className="font-heading text-3xl font-bold text-foreground mb-2">Welcome Back</h1>
          <p className="font-body text-muted-foreground">Sign in to your account to continue</p>
        </div>

        <div className="bg-card rounded-lg shadow-elevation-3 p-8">
          {showMfaStep ? (
            <MfaVerificationStep
              factorId={mfaFactorId}
              onVerified={handleMfaVerified}
              onCancel={handleMfaCancel}
            />
          ) : (
            <>
              {serverError && (
                <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start space-x-3">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" strokeWidth={2} />
                  <p className="font-body text-sm text-error flex-1">{serverError}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <Input
                  type="email"
                  name="email"
                  label="Email Address"
                  placeholder="you@example.com"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                />

                <Input
                  type="password"
                  name="password"
                  label="Password"
                  placeholder="Enter your password"
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                />

                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    />
                    <span className="font-body text-sm text-foreground">Remember me</span>
                  </label>
                  <Link
                    to="/forgot-password"
                    className="font-body text-sm text-primary hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  fullWidth
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  iconName="LogIn"
                >
                  {isSubmitting ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="font-body text-sm text-center text-muted-foreground mb-4">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-primary font-medium hover:underline">
                    Sign up
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="font-caption text-xs text-muted-foreground">
            By signing in, you agree to our{' '}
            <Link to="#" className="text-primary hover:underline">Terms of Service</Link>
            {' '}and{' '}
            <Link to="#" className="text-primary hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;